import { useEffect } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { App as AntdApp, ConfigProvider } from 'antd';
import { Toaster } from 'sonner';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import FeedbackButton from './components/FeedbackButton';
import { initBehaviorTracking } from './services/behaviorTracker';
import HomePage from './pages/HomePage';
import LoadingPage from './pages/LoadingPage';
import InterviewPage from './pages/InterviewPage';
import ReportPage from './pages/ReportPage';
import HistoryPage from './pages/HistoryPage';
import SettingsPage from './pages/SettingsPage';
import LeaderboardPage from './pages/LeaderboardPage';
import ChatPage from './pages/ChatPage';
import ResumeOptimizePage from './pages/ResumeOptimizePage';
import ResumeGeneratePage from './pages/ResumeGeneratePage';
import ResumeEditPage from './pages/ResumeEditPage';
import SalaryNegotiationPage from './pages/SalaryNegotiationPage';
import SalaryReportPage from './pages/SalaryReportPage';
import { useAppDispatch } from './store/hooks';
import { resetInterview } from './store/interviewSlice';

// 根据当前路由决定是否显示导航栏和Footer
function LayoutWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Loading、Interview、SalaryNegotiation、SalaryLoading 页面不显示导航栏和Footer
  const hideLayoutPaths = [
    '/loading',
    '/interview',
    '/salary-negotiation',
    '/salary-loading',
  ];
  const shouldShowLayout = !hideLayoutPaths.includes(location.pathname);

  // 页面切换时滚动到顶部
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
    });
  }, [location.pathname]);

  // 监听登出事件，清理面试状态
  useEffect(() => {
    const handleLogout = () => {
      dispatch(resetInterview());
      navigate('/', { replace: true });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => {
      window.removeEventListener('auth:logout', handleLogout);
    };
  }, [dispatch, navigate]);

  return (
    <>
      {shouldShowLayout && <Navbar />}
      {children}
      {shouldShowLayout && <Footer />}
    </>
  );
}

function App() {
  // 初始化行为追踪 - 使用 useEffect 确保只在组件挂载时执行一次
  useEffect(() => {
    initBehaviorTracking();
  }, []);

  return (
    <ConfigProvider
      theme={{
        components: {
          Message: {
            contentBg: '#1a1f3a',
            colorText: '#fff',
          },
        },
      }}
    >
      <AntdApp>
        <BrowserRouter>
          <LayoutWrapper>
            <Routes>
              {/* 首页 - 面试设置 */}
              <Route path="/" element={<HomePage />} />

              {/* 过渡页 - 加载中 */}
              <Route path="/loading" element={<LoadingPage />} />

              {/* 视频面试页 */}
              <Route path="/interview" element={<InterviewPage />} />

              {/* 报告页 - 支持带 ID 和不带 ID 两种形式 */}
              <Route path="/report" element={<ReportPage />} />
              <Route path="/report/:id" element={<ReportPage />} />

              {/* 面试历史页 */}
              <Route path="/history" element={<HistoryPage />} />

              {/* 个人设置页 */}
              <Route path="/settings" element={<SettingsPage />} />

              {/* 排行榜页 */}
              <Route path="/leaderboard" element={<LeaderboardPage />} />

              {/* 聊一聊页面 */}
              <Route path="/chat" element={<ChatPage />} />

              {/* 简历生成页面 */}
              <Route path="/resume-generate" element={<ResumeGeneratePage />} />

              {/* 简历编辑页面 */}
              <Route path="/resume-edit" element={<ResumeEditPage />} />

              {/* 简历评估页面 */}
              <Route path="/resume-optimize" element={<ResumeOptimizePage />} />

              {/* 谈薪模拟页面 */}
              <Route
                path="/salary-negotiation"
                element={<SalaryNegotiationPage />}
              />

              {/* 谈薪报告页面 */}
              <Route path="/salary-report" element={<SalaryReportPage />} />
              <Route path="/salary-report/:id" element={<SalaryReportPage />} />

              {/* 404 重定向到首页 */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </LayoutWrapper>
          {/* 产品反馈悬浮按钮 */}
          <FeedbackButton />
          <Toaster position="top-center" />
        </BrowserRouter>
      </AntdApp>
    </ConfigProvider>
  );
}

export default App;
